/**
 * CallRail PII Redaction Helper (Story 22.1, AC #5; Round 3 Red Team Attack #1)
 *
 * **NEW for CallRail — no analogue in prior API-key adapters** (Ahrefs 20.1,
 * Semrush 20.4, Moz 20.7).
 *
 * Unlike SEO tools where the API surface contains only business-aggregate
 * metrics, CallRail's `/calls.json` and `/form_submissions.json` endpoints
 * return per-call records with **caller-identifying data** in the
 * DEFAULT response (verified `apidocs.callrail.com/` 2026-05-14 — the
 * default field list includes `customer_phone_number`, `customer_name`,
 * `customer_city`, `customer_state`, `customer_country`, `recording`).
 *
 * The adapter MUST run every record through `sanitizeCallRailPii` BEFORE
 * the record enters:
 *  - `console.log` (structured log emissions)
 *  - Sentry breadcrumbs / `Sentry.captureException` context
 *  - Queue-error payloads (CloudFlare Queues dead-letter messages)
 *  - Any error message body returned to a downstream consumer
 *
 * The redacted-form record is the only shape allowed to leak. The
 * unredacted-form is in scope ONLY inside `fetchMetrics` → transformer →
 * return path — that path eventually writes to the tenant-scoped KV cache
 * and D1, where the customer's tenant access control governs read access.
 *
 * **PII field list — verified against the catalogue at
 * `_bmad/bmm/data/integration-metrics/callrail.md` 2026-05-14:**
 *  - `customer_phone_number` — caller's phone (direct PII)
 *  - `customer_name` — caller's name (direct PII)
 *  - `customer_email` — caller's email (direct PII)
 *  - `customer_city` — reasonable PII when combined with other fields
 *  - `customer_state` — reasonable PII when combined with other fields
 *  - `customer_country` — geo-resolution can be PII when combined
 *  - `formatted_customer_location` — composite of the above
 *  - `transcription` — contains the caller's spoken words (PII)
 *  - `conversational_transcript` — same as above, structured form
 *  - `voicemail_transcription` — caller's voicemail words
 *  - `call_summary` — AI-generated call summary, may contain caller
 *    identification
 *  - `call_highlights` — AI-extracted highlights, same risk
 *  - `keywords_spotted` — array of spoken phrases that triggered alerts
 *
 * **The recording URL (`recording` field) is NOT redacted by this helper.**
 * Recording URLs are stripped at the transformer (Story 22.1 AC #4 +
 * Round 3 Red Team Attack #2) — they never enter the persisted record
 * shape at all, so they cannot leak via this path.
 */

/**
 * Frozen Set of PII field names to redact. Defined as a Set for O(1)
 * `.has()` lookups during recursive traversal. Adding a new field is
 * additive; removing a field requires explicit justification + audit
 * (Round 3 Red Team Attack #1).
 */
export const CALLRAIL_PII_FIELDS: ReadonlySet<string> = new Set<string>([
  "customer_phone_number",
  "customer_name",
  "customer_email",
  "customer_city",
  "customer_state",
  "customer_country",
  "formatted_customer_location",
  "transcription",
  "conversational_transcript",
  "voicemail_transcription",
  "call_summary",
  "call_highlights",
  "keywords_spotted",
]);

/**
 * The lowerCamelCase forms of the PII fields above — used after the
 * transformer has normalized snake_case → lowerCamelCase. Both
 * representations are checked because PII flows through the helper at
 * BOTH the pre-transform stage (wire-format snake_case from CallRail) and
 * the post-transform stage (records emitted to logs).
 */
export const CALLRAIL_PII_FIELDS_CAMEL: ReadonlySet<string> = new Set<string>([
  "customerPhoneNumber",
  "customerName",
  "customerEmail",
  "customerCity",
  "customerState",
  "customerCountry",
  "formattedCustomerLocation",
  "transcription",
  "conversationalTranscript",
  "voicemailTranscription",
  "callSummary",
  "callHighlights",
  "keywordsSpotted",
]);

/**
 * Redact PII fields from a CallRail record. Returns a NEW shallow copy —
 * never mutates the input.
 *
 * Recursive: handles the nested `customer: { phone_number, ... }` shape
 * that some CallRail webhook payloads use. Caps recursion depth at 4 to
 * defend against pathological self-referential inputs.
 *
 * Replacement value is the literal string `"[redacted]"` for all
 * redacted fields. We do NOT preserve type information (replacing a
 * string with a string vs replacing an array with a string) because the
 * redacted form is intended for LOG OUTPUT only — never for consumption
 * by downstream logic that would type-check the field. If a downstream
 * consumer needs the unredacted value, it reads from the
 * `fetchMetrics` return path BEFORE this helper runs.
 *
 * @param record - The raw record from CallRail (or a transformed record).
 * @param depth - Internal recursion guard. Callers should NOT pass this.
 * @returns A shallow-copied record with PII fields replaced by `"[redacted]"`.
 */
/**
 * Reserved JS-runtime keys that, if assigned via `out[key] = ...`,
 * could mutate `Object.prototype` (in non-strict mode) or otherwise
 * confuse downstream consumers that perform property-existence checks.
 *
 * Pass 2 review fix: a malicious CallRail user could craft a custom
 * field name like `__proto__` or `constructor` on their account.
 * Object.entries returns these as own-props on the wire-format object;
 * blindly copying them via `out[key] = value` is an attack surface.
 * Skip them entirely.
 */
const FORBIDDEN_KEYS: ReadonlySet<string> = new Set([
  "__proto__",
  "constructor",
  "prototype",
]);

export function sanitizeCallRailPii<T extends Record<string, unknown>>(
  record: T,
  depth = 0,
): Record<string, unknown> {
  // Defensive recursion cap — pathological inputs cannot exhaust stack.
  if (depth > 4 || record === null || typeof record !== "object") {
    return record as Record<string, unknown>;
  }

  // Pass 3 review fix: use `{}` (plain object) for the output, not
  // `Object.create(null)`. The null-prototype variant breaks downstream
  // consumers that expect `obj instanceof Object === true` (Sentry
  // breadcrumbs, some structured-log libs, postMessage cloning). The
  // FORBIDDEN_KEYS skip below is the actual defense against
  // prototype pollution; the null-prototype was redundant belt-and-
  // suspenders that introduced new risk.
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(record)) {
    // Pass 2 review fix: skip reserved JS-runtime keys.
    if (FORBIDDEN_KEYS.has(key)) continue;
    if (
      CALLRAIL_PII_FIELDS.has(key) ||
      CALLRAIL_PII_FIELDS_CAMEL.has(key)
    ) {
      out[key] = "[redacted]";
      continue;
    }

    // Pass 1 review fix: Date / Map / Set are `typeof === "object"` but
    // recursing into them via Object.entries returns empty / nonsense,
    // dropping the original value. Pass these through unchanged.
    if (
      value instanceof Date ||
      value instanceof Map ||
      value instanceof Set ||
      value instanceof RegExp ||
      value instanceof Error
    ) {
      out[key] = value;
      continue;
    }

    // Recurse into nested objects (common CallRail webhook shape: `customer: { ... }`).
    if (value !== null && typeof value === "object" && !Array.isArray(value)) {
      out[key] = sanitizeCallRailPii(
        value as Record<string, unknown>,
        depth + 1,
      );
      continue;
    }

    // Arrays of objects (rare in CallRail, but defensive — `tags` is an
    // array of strings, `milestones` is an array of objects).
    if (Array.isArray(value)) {
      out[key] = value.map((item) =>
        item !== null && typeof item === "object"
          ? sanitizeCallRailPii(item as Record<string, unknown>, depth + 1)
          : item,
      );
      continue;
    }

    out[key] = value;
  }
  return out;
}

/**
 * Strip CSV-injection prefix characters from a string value. Round 3
 * Red Team Attack #4 — defensive against pathological customer-supplied
 * data (e.g. `customer_name = "=cmd|/c calc!A1"`) that would execute as
 * a formula in Excel/Google Sheets when the customer exports the report.
 *
 * Mirrors the Moz 20.7 `stripCsvInjectionPrefix` shape (which CallRail
 * could LITERALLY reuse — but per the OAuth-Integration directive's
 * anti-pattern §1, we keep adapter-internal utilities self-contained to
 * avoid cross-adapter coupling). Future refactor opportunity: lift to
 * a shared `packages/integrations/src/safe-export.ts` if a third
 * consumer emerges.
 *
 * LOOP-strips to handle nested triggers like `=+@cmd` → `cmd` (Round 5
 * Edge Case Hunter finding).
 */
export function stripCsvInjectionPrefix(value: string): string {
  if (typeof value !== "string" || value.length === 0) return value;
  // CSV-injection triggers per OWASP "Formula Injection" reference:
  // `=`, `+`, `@`, `-`, tab, CR. LOOP-strip so adversarial cascades
  // like `==+@cmd` resolve to `cmd`.
  let out = value;
  // Pass 2 review fix: include `\n` in the trigger set. A leading
  // `\r\n` previously consumed only the `\r` and left `\n=cmd` —
  // newline-leading content in a quoted CSV cell can still introduce
  // a multi-line formula injection in some BI tools.
  while (out.length > 0 && /^[=+@\-\t\r\n]/.test(out)) {
    out = out.slice(1);
  }
  return out;
}
